import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Button,
  Alert,
  Divider,
  Stack,
} from "@mui/material";
import { AppContext } from "../../../context/AppContext";
import FinancialOverview from "./FinancialOverview";
import Banks from "./Banks";

const BLUE = "#1565C0";
const BLUE_DARK = "#0D47A1";
const TEXT = "#0f172a";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";

const money = (value) => {
  const n = Number(value || 0);
  return `$${n.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const percent = (value) => {
  const n = Number(value || 0);
  return `${n.toFixed(2)}%`;
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const MetricBox = ({ title, value, helper, loading, main }) => {
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 3,
        border: `1px solid ${BORDER}`,
        boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
        bgcolor: "#fff",
        height: "100%",
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: MUTED,
          mb: 0.8,
        }}
      >
        {title}
      </Typography>

      {loading ? (
        <CircularProgress size={22} />
      ) : (
        <Typography
          sx={{
            fontSize: { xs: 22, md: 27 },
            fontWeight: 800,
            color: main ? BLUE : TEXT,
            lineHeight: 1.1,
          }}
        >
          {value}
        </Typography>
      )}

      <Typography
        sx={{
          mt: 0.8,
          color: MUTED,
          fontSize: 12.5,
          lineHeight: 1.35,
          fontWeight: 500,
        }}
      >
        {helper}
      </Typography>
    </Paper>
  );
};

const PeriodButton = ({ active, children, onClick }) => {
  return (
    <Button
      onClick={onClick}
      disableElevation
      variant={active ? "contained" : "outlined"}
      sx={{
        textTransform: "none",
        bgcolor: active ? BLUE : "#fff",
        color: active ? "#fff" : BLUE,
        "&:hover": {
          bgcolor: active ? BLUE_DARK : "#eef5ff",
          borderColor: BLUE_DARK,
        },
        borderColor: "#cbd5e1",
        borderRadius: 2,
        px: 2.2,
        fontWeight: 700,
        boxShadow: active ? "0 8px 20px rgba(21,101,192,0.22)" : "none",
      }}
    >
      {children}
    </Button>
  );
};

const BusinessDashboard = () => {
  const { accessToken } = useContext(AppContext);

  const [periodo, setPeriodo] = useState("mes");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const endpoint = useMemo(() => {
    if (periodo === "mes") {
      return `/dashboard/empresa/?periodo=mes&mes=${getCurrentMonth()}`;
    }

    return `/dashboard/empresa/?periodo=${periodo}`;
  }, [periodo]);

  useEffect(() => {
    const loadDashboard = async () => {
      if (!accessToken) return;

      setLoading(true);
      setError("");

      try {
        const res = await fetch(endpoint, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        const contentType = res.headers.get("content-type") || "";
        let json = null;

        if (contentType.includes("application/json")) {
          json = await res.json();
        } else {
          await res.text();
          throw new Error(`Respuesta no JSON desde ${endpoint}. Estado HTTP ${res.status}.`);
        }

        if (!res.ok) {
          throw new Error(json?.error || `Error HTTP ${res.status}`);
        }

        setData(json);
      } catch (err) {
        setData(null);
        setError(err.message || "No se pudo cargar el dashboard.");
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [accessToken, endpoint]);

  const summary = data?.resumen || {};

  const diagnostic = useMemo(() => {
    const utilidad = Number(summary.utilidad_aproximada || 0);
    const flujo = Number(summary.flujo_neto || 0);
    const porCobrar = Number(summary.cuentas_por_cobrar || 0);
    const porPagar = Number(summary.cuentas_por_pagar || 0);
    const margen = Number(summary.margen_aproximado || 0);

    if (!data) return "";

    if (utilidad < 0) {
      return "El período muestra pérdida aproximada: compras y gastos superan las ventas netas.";
    }

    if (flujo < 0 && porCobrar > 0) {
      return "Hay ventas registradas, pero el flujo está presionado por cartera pendiente de cobro.";
    }

    if (porPagar > porCobrar && porPagar > 0) {
      return "Las obligaciones con proveedores superan la cartera por cobrar. Revisa pagos próximos.";
    }

    if (margen > 0 && flujo >= 0) {
      return "El período se mantiene estable: resultado aproximado positivo y flujo no negativo.";
    }

    return "Revisa ventas, compras, cartera y flujo para tener una lectura completa del período.";
  }, [data, summary]);

  return (
    <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
      <Paper
        sx={{
          p: 2.5,
          borderRadius: 3,
          boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
          mb: 2.2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 19, color: TEXT }}>
              Estado de la empresa
            </Typography>

            <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.4 }}>
              Resumen general de ventas, compras, cartera, inventario y flujo financiero
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <PeriodButton active={periodo === "semana"} onClick={() => setPeriodo("semana")}>
              Semana
            </PeriodButton>

            <PeriodButton active={periodo === "mes"} onClick={() => setPeriodo("mes")}>
              Mes
            </PeriodButton>

            <PeriodButton active={periodo === "anio"} onClick={() => setPeriodo("anio")}>
              Año
            </PeriodButton>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        ) : (
          <Alert
            severity={Number(summary.utilidad_aproximada || 0) < 0 ? "warning" : "info"}
            sx={{
              borderRadius: 2,
              bgcolor: Number(summary.utilidad_aproximada || 0) < 0 ? "#fff7ed" : "#eef5ff",
              color: TEXT,
              border: Number(summary.utilidad_aproximada || 0) < 0 ? "1px solid #fed7aa" : "1px solid #bfdbfe",
              "& .MuiAlert-icon": {
                color: Number(summary.utilidad_aproximada || 0) < 0 ? "#c2410c" : BLUE,
              },
            }}
          >
            {loading ? "Cargando contexto financiero..." : diagnostic}
          </Alert>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricBox
            title="Ventas netas"
            value={money(summary.ventas_netas)}
            helper={`Brutas ${money(summary.ventas_brutas)} · NC ${money(summary.notas_credito)}`}
            loading={loading}
            main
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricBox
            title="Resultado aproximado"
            value={money(summary.utilidad_aproximada)}
            helper={`Margen estimado ${percent(summary.margen_aproximado)}`}
            loading={loading}
            main
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricBox
            title="Cartera"
            value={money(summary.cuentas_por_cobrar)}
            helper={`Por pagar ${money(summary.cuentas_por_pagar)} · Créditos ${money(summary.creditos_clientes)}`}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricBox
            title="Flujo de caja"
            value={money(summary.flujo_neto)}
            helper={`Cobros ${money(summary.cobros_periodo)} · Pagos ${money(summary.pagos_periodo)}`}
            loading={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <FinancialOverview data={data} loading={loading} />
        </Grid>

        <Grid item xs={12}>
          <Banks accessToken={accessToken} periodo={periodo} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BusinessDashboard;